import { api } from './config';
import { ApiResponse } from '../types';

/**
 * Feedback API Factory
 *
 * Admin과 Brand에서 공통으로 사용하는 Feedback API 메서드를 생성합니다.
 * 중복 코드를 줄이고 일관된 API 인터페이스를 제공합니다.
 */

export interface FeedbackCreateData {
  title: string;
  description: string;
  category: string;
  priority: string;
  // Admin-specific
  submittedById?: number;
  // Brand-specific
  brandId?: number;
  submitterName?: string;
  submitterEmail?: string;
}

export interface FeedbackUpdateData {
  title?: string;
  description?: string;
  category?: string;
  priority?: string;
  status?: string;
  adminNotes?: string;
}

export interface FeedbackReplyData {
  message: string;
  // Admin-specific
  adminId?: number;
  // Brand-specific
  brandId?: number;
  senderName: string;
  senderEmail: string;
}

export interface FeedbackApiMethods {
  getFeedback: (params?: Record<string, any>) => Promise<any>;
  getFeedbackById: (id: number) => Promise<any>;
  createFeedback: (data: FeedbackCreateData) => Promise<any>;
  updateFeedback: (id: number, data: FeedbackUpdateData) => Promise<any>;
  deleteFeedback: (id: number, additionalParams?: Record<string, any>) => Promise<any>;
  getFeedbackReplies: (feedbackId: number) => Promise<any>;
  addFeedbackReply: (feedbackId: number, data: FeedbackReplyData) => Promise<any>;
}

/**
 * Feedback API 팩토리 함수
 *
 * @param baseUrl - API 기본 경로 ('/api/admin' 또는 '/api/brand')
 * @returns Feedback API 메서드 객체
 */
export const createFeedbackApi = (baseUrl: '/admin' | '/brand'): FeedbackApiMethods => ({
  getFeedback: (params?: Record<string, any>) =>
    api.get<ApiResponse>(`${baseUrl}/feedback`, { params }),

  getFeedbackById: (id: number) =>
    api.get<ApiResponse>(`${baseUrl}/feedback/${id}`),

  createFeedback: (data: FeedbackCreateData) =>
    api.post<ApiResponse>(`${baseUrl}/feedback`, data),

  updateFeedback: (id: number, data: FeedbackUpdateData) =>
    api.put<ApiResponse>(`${baseUrl}/feedback/${id}`, data),

  deleteFeedback: (id: number, additionalParams?: Record<string, any>) =>
    api.delete<ApiResponse>(`${baseUrl}/feedback/${id}`, { params: additionalParams }),

  getFeedbackReplies: (feedbackId: number) =>
    api.get<ApiResponse>(`${baseUrl}/feedback/${feedbackId}/replies`),

  addFeedbackReply: (feedbackId: number, data: FeedbackReplyData) =>
    api.post<ApiResponse>(`${baseUrl}/feedback/${feedbackId}/replies`, data),
});

// Pre-configured API instances
export const adminFeedbackApi = createFeedbackApi('/admin');
export const brandFeedbackApi = createFeedbackApi('/brand');
